import TagManager from 'react-gtm-module';

import { ATC_SOURCES, type AtcSource } from '@utils/cart/atcSource';
import {
  DeferredNavigationProps,
  deferredNavigationFields,
} from './clickTracking';

/** GA4 `item_list_name` for every add-to-cart from the PDP "Why Choose
    [Brand]" quick add chip (ED-422) — one constant for every section; the
    brand is reported separately as `item_brand` (product) and `coop_brand`
    (section). */
export const WHY_CHOOSE_ITEM_LIST_NAME = 'Why Choose - PDP';

/** The page type a Why Choose section renders on. */
export type WhyChoosePageType = 'pdp' | 'homepage' | 'cip' | 'clp';

/** GA4 `atc_source` and `item_list_name` for an add-to-cart from a homepage,
    CIP or CLP Why Choose chip — both carry the same per-page value, as the
    Image Hotspot does (ED-586, ED-590). The PDP keeps its ED-422 values:
    `atc_source` from the `'why_choose'` source and `WHY_CHOOSE_ITEM_LIST_NAME`. */
export const WHY_CHOOSE_ATC_SOURCE_BY_PAGE_TYPE: Record<
  Exclude<WhyChoosePageType, 'pdp'>,
  AtcSource
> = {
  homepage: ATC_SOURCES.HOMEPAGE_WHY_CHOOSE,
  cip: ATC_SOURCES.CIP_WHY_CHOOSE,
  clp: ATC_SOURCES.CLP_WHY_CHOOSE,
};

/** GA4 `type` (ED-590) on the click events. Sent for the homepage, CIP and
    CLP only, so the PDP events keep their ED-422 shape. */
const typeField = (pageType: WhyChoosePageType) =>
  pageType === 'pdp' ? {} : { type: pageType };

export interface TrackWhyChooseCtaClickProps extends DeferredNavigationProps {
  data: {
    /** Path + query the click happened on, e.g.
        `/en/product/6036-768/mec-camper-3-person-tent?colour=Butte+Green` */
    interactionLocation: string;
    /** Resolved href of the "Shop [brand]" link. */
    linkUrl: string;
    linkText: string;
    coOpBrand: string | null;
    pageType: WhyChoosePageType;
  };
}

/**
 * ED-422 — the section's "Shop [brand]" link. A real `<a href>` navigation,
 * so it supports `eventCallback`/`eventTimeout` to defer navigation until the
 * hit is sent, same as `trackHotspotCtaClick`.
 */
export const trackWhyChooseCtaClick = ({
  data,
  ...deferral
}: TrackWhyChooseCtaClickProps) => {
  const { interactionLocation, linkUrl, linkText, coOpBrand, pageType } = data;

  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'click_why_choose_cta',
      interaction: {
        interaction_location: interactionLocation,
        link_url: linkUrl,
        link_text: linkText,
        coop_brand: coOpBrand,
        ...typeField(pageType),
      },
      ...deferredNavigationFields(deferral),
    },
  });
};

export interface TrackWhyChooseQuickAddClickProps {
  data: {
    interactionLocation: string;
    coOpBrand: string | null;
    /** The featured product's code (`parentSku`), e.g. `6036-730`. */
    productId: string;
    pageType: WhyChoosePageType;
  };
}

/**
 * ED-422 — fired when the quick add chip's trigger opens the popover (the
 * same moment `click_quick_atc` / `click_hotspot_quick_atc` fire), replacing
 * Quick Add's default `click_quick_atc` via its `onTriggerOpen` override.
 */
export const trackWhyChooseQuickAddClick = ({
  data,
}: TrackWhyChooseQuickAddClickProps) => {
  const { interactionLocation, coOpBrand, productId, pageType } = data;

  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'click_why_choose_atc',
      interaction: {
        interaction_location: interactionLocation,
        coop_brand: coOpBrand,
        product_id: productId,
        ...typeField(pageType),
      },
    },
  });
};
